/* eslint-disable max-lines */
import {
  Badge,
  LoadingSpinner,
  Pill,
  Text,
  Title,
  useModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack, type Theme, ThemeProvider } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PullToRefresh } from "@src/appV2/lib/PullToRefresh/PullToRefresh";
import { convertToGeoLocation, type GeoLocation } from "@src/appV2/Location";
import { CollapsiblePageHeader } from "@src/appV2/redesign/components/CollapsiblePageHeader";
import { FloatingNavigationButton } from "@src/appV2/redesign/components/FloatingNavigationButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { EARNINGS_FULL_PATH } from "@src/appV2/redesign/Payments/Earnings/paths";
import { SHIFT_DISCOVERY_LIST_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { WorkplaceModalsDataProvider } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { ExtraWorkedTimeRequestStatus } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import { useShouldBeOnboardedToStripe } from "@src/appV2/Stripe/useShouldBeOnboardedToStripe";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { addHours, differenceInMinutes, isAfter, parseISO } from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { generatePath, useHistory } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";

import { IconButton } from "../../components/IconButton";
import {
  type ExtraWorkedTimeRequestType,
  usePaginatedWorkerShifts,
} from "../api/usePaginatedWorkerShifts";
import { PageContent } from "../components/PageContent";
import { ReturnForSignatureBottomSheet } from "../components/ReturnForSignatureBottomSheet";
import { useMissedPunchRequestGeofenceCheck } from "../hooks/useMissedPunchRequestGeofenceCheck";
import {
  PAST_SHIFT_DETAILS_FULL_PATH,
  SHIFT_HISTORY_FULL_PATH,
  SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH,
} from "../path";
import {
  SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY,
  UNVERIFIED_TIMESHEET_RETURN_PATH_STATE_KEY,
} from "../ShiftDetails/constants";
import { UploadTimesheetBottomSheet } from "../UnverifiedShifts/UploadTimesheet/UploadTimesheetBottomSheet";
import { ShiftHistoryCard } from "./Card";
import { ChatWithSupportBottomSheet } from "./ChatWithSupportBottomSheet";
import { ShiftHistoryBannerList } from "./components/ShiftHistoryBannerList";
import { getRelevantExtraWorkedTimeRequest } from "./components/ShiftHistoryListItem/getShiftHistoryListItemVariant";
import { ShiftHistoryListItem } from "./components/ShiftHistoryListItem/ShiftHistoryListItem";
import { ShiftHistoryEmptyState } from "./EmptyState";
import { ShiftHistoryFiltersBottomSheet } from "./Filters/BottomSheet/BottomSheet";
import { type FiltersFormValues, useFiltersForm } from "./Filters/BottomSheet/useFiltersForm";
import { ShiftHistoryLoadingPageState } from "./LoadingPageState";

export interface ShiftActionParams {
  shiftId: string;
  workplaceId: string | undefined;
  workplaceLocation: GeoLocation | undefined;
  shiftEndAt: string | undefined;
  isVerified: boolean;
  isTimesheetUploaded: boolean;
}

export function ShiftHistoryPage() {
  const worker = useDefinedWorker();
  const history = useHistory();

  const isShiftHistoryPageRedesignEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_SHIFT_HISTORY_PAGE_REDESIGN,
    { defaultValue: false }
  );
  const shouldOnboardToStripe = useShouldBeOnboardedToStripe();

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_HISTORY_PAGE_VIEWED,
    { workerId: worker.userId },
    { enabled: isDefined(worker.userId) }
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const uploadTimesheetModalState = useModalState();
  const chatWithSupportModalState = useModalState();
  const returnForSignatureModalState = useModalState();

  const [selectedShift, setSelectedShift] = useState<ShiftActionParams | undefined>(undefined);

  const { form, changedFiltersCount, resetFilters, cancelFilters, submitFilters, appliedFilters } =
    useFiltersForm();

  const {
    data: shiftHistoryPages,
    isLoading: isInitialLoading,
    isSuccess,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchShifts,
  } = usePaginatedWorkerShifts(
    {
      workerId: worker.userId,
      timeline: "PAST_AND_IN_PROGRESS",
      sortBy: "schedule.startAt",
      sortOrder: "desc",
      pageSize: 5,
      fields: {
        shift: [
          "schedule.startAt",
          "schedule.endAt",
          "schedule.timeSlot",
          "schedule.durationWithoutBreaksInHours",
          "requirements.qualificationName",
          "pay.total",
          "clockInOut.start",
          "clockInOut.end",
          "lunchInOut.start",
          "lunchInOut.end",
          "verified",
          "isMissedPunchRequestEnabled",
          "timecard.files",
          "shiftEndGraceTimeHours",
        ],
        workplace: ["name", "timezone", "geoLocation"],
        extraWorkedTimeRequest: [
          "type",
          "status",
          "statusUpdatedBy",
          "isFullShiftPayment",
          "timeRequestedInMinutes",
          "createdAt",
          "start",
          "end",
        ],
      },
      include: ["workplace", "extra-worked-time-request"],
      filter: {
        deleted: false,
        ...(appliedFilters.shiftTimeSlots.length > 0 && {
          timeSlot: appliedFilters.shiftTimeSlots.join(","),
        }),
        ...(appliedFilters.licenses.length > 0 && {
          qualificationName: appliedFilters.licenses.join(","),
        }),
      },
    },
    {
      enabled: isDefined(worker.userId),
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
    }
  );

  const workplaceMap = useMemo(() => {
    const map = new Map<string, { name?: string; timezone?: string; geoLocation?: GeoLocation }>();
    shiftHistoryPages?.pages.forEach((page) => {
      page.included?.forEach((item) => {
        if (item.type === "workplace") {
          const coordinates = item.attributes?.geoLocation?.coordinates;
          const geoLocation = isDefined(coordinates)
            ? convertToGeoLocation(coordinates)
            : undefined;
          map.set(item.id, {
            name: item.attributes?.name,
            timezone: item.attributes?.timezone,
            geoLocation,
          });
        }
      });
    });
    return map;
  }, [shiftHistoryPages]);

  // Geofence check hook for MPR
  const { isCheckingGeofence, checkGeofence } = useMissedPunchRequestGeofenceCheck();

  // Navigate to submit timesheet page with or without signature
  const navigateToSubmitTimesheet = useCallback(
    (shiftId: string, skipWorkplaceSignature: boolean) => {
      const searchParams = new URLSearchParams({
        [SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY]: String(skipWorkplaceSignature),
      });
      history.push({
        pathname: generatePath(SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH, {
          shiftId,
        }),
        search: searchParams.toString(),
        state: { [UNVERIFIED_TIMESHEET_RETURN_PATH_STATE_KEY]: SHIFT_HISTORY_FULL_PATH },
      });
    },
    [history]
  );

  const handleUploadTimesheetClick = useCallback(
    (params: ShiftActionParams) => {
      setSelectedShift(params);
      logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_TAPPED, {
        shiftId: params.shiftId,
      });
      uploadTimesheetModalState.openModal();
    },
    [uploadTimesheetModalState]
  );

  const handleChatWithSupportClick = useCallback(
    (params: ShiftActionParams) => {
      setSelectedShift(params);
      chatWithSupportModalState.openModal();
    },
    [chatWithSupportModalState]
  );

  const handleShiftClick = useCallback(
    (shiftId: string) => {
      history.push(generatePath(PAST_SHIFT_DETAILS_FULL_PATH, { shiftId }));
    },
    [history]
  );

  // Wrapper function to validate shift data and perform geofence check
  const validateAndCheckGeofence = useCallback(
    async (
      params: ShiftActionParams
    ): Promise<{
      status: "inside" | "outside" | "unknown";
      error?: string;
    }> => {
      const { shiftId, workplaceId, workplaceLocation, shiftEndAt } = params;

      if (!isDefined(workplaceLocation)) {
        return { status: "unknown", error: "No workplace location" };
      }

      if (!isDefined(shiftEndAt)) {
        return { status: "unknown", error: "No shift end time" };
      }

      const result = await checkGeofence({
        shiftId,
        shiftEnd: shiftEndAt,
        workerId: worker.userId,
        workplaceId,
        workplaceLocation,
      });

      return { status: result.status };
    },
    [checkGeofence, worker.userId]
  );

  // Handle the MPR request flow with geofence check
  const handleRequestShiftAdjustment = useCallback(
    async (params: ShiftActionParams) => {
      setSelectedShift(params);
      const result = await validateAndCheckGeofence(params);

      if (result.status === "inside" || result.status === "unknown") {
        navigateToSubmitTimesheet(params.shiftId, false);
        setSelectedShift(undefined);
      } else {
        logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, {
          shiftId: params.shiftId,
          workerId: worker.userId,
        });
        returnForSignatureModalState.openModal();
      }
    },
    [
      validateAndCheckGeofence,
      navigateToSubmitTimesheet,
      worker.userId,
      returnForSignatureModalState,
    ]
  );

  const handleRecheckLocation = useCallback(async () => {
    if (!isDefined(selectedShift)) {
      return;
    }

    const result = await validateAndCheckGeofence(selectedShift);

    if (result.status === "inside" || result.status === "unknown") {
      returnForSignatureModalState.closeModal();
      navigateToSubmitTimesheet(selectedShift.shiftId, false);
      setSelectedShift(undefined);
    }
  }, [
    selectedShift,
    validateAndCheckGeofence,
    returnForSignatureModalState,
    navigateToSubmitTimesheet,
  ]);

  const handleConfirmWithoutSignature = useCallback(() => {
    if (isDefined(selectedShift)) {
      navigateToSubmitTimesheet(selectedShift.shiftId, true);
    }
    setSelectedShift(undefined);
  }, [selectedShift, navigateToSubmitTimesheet]);

  const extraWorkedTimeRequestMap = useMemo(() => {
    const map = new Map<
      string,
      {
        attributes: ExtraWorkedTimeRequestType["attributes"];
        meta: ExtraWorkedTimeRequestType["meta"];
      }
    >();
    shiftHistoryPages?.pages.forEach((page) => {
      page.included?.forEach((item) => {
        if (item.type === "extra-worked-time-request") {
          map.set(item.id, { attributes: item.attributes, meta: item.meta });
        }
      });
    });
    return map;
  }, [shiftHistoryPages]);

  const { filteredShifts: shiftHistory, excludedCount: clientFilteredShiftCount } = useMemo(() => {
    const allShifts = shiftHistoryPages?.pages.flatMap((page) => page.data) ?? [];

    // Filter out shifts that are still within the grace period and don't have a timecard or ewtr
    // These shifts are still visible in the bookings page
    const filteredShifts = allShifts.filter((shift) => {
      const shiftEndAt = shift.attributes?.schedule?.endAt;
      const hasTimecard = (shift.attributes?.timecard?.files?.length ?? 0) > 0;

      // If shift has a timecard, always show it in history
      if (hasTimecard) {
        return true;
      }

      // If shift has a FULL_SHIFT MPR (Missed Punch Request), show it in history
      // This handles the Missed Punch Request "without signature" flow where shifts disappear from My Bookings
      // but don't have a timecard yet
      const hasMissedPunchRequest =
        shift.relationships?.extraWorkedTimeRequest?.data?.some(({ id }) => {
          const request = extraWorkedTimeRequestMap.get(id);
          return isDefined(request) && request.attributes?.type === ExtraWorkedTimeType.FULL_SHIFT;
        }) ?? false;

      if (hasMissedPunchRequest) {
        return true;
      }

      // If no end time, include the shift
      if (!isDefined(shiftEndAt)) {
        return true;
      }

      const graceTimeHours = shift.attributes?.shiftEndGraceTimeHours ?? 6;

      // If shift is still within the grace period (end + graceHours > now), hide it
      // because it's still visible in the bookings page
      const shiftEndWithGrace = addHours(parseISO(shiftEndAt), graceTimeHours);
      return isAfter(new Date(), shiftEndWithGrace);
    });

    return {
      filteredShifts,
      excludedCount: allShifts.length - filteredShifts.length,
    };
  }, [shiftHistoryPages, extraWorkedTimeRequestMap]);

  const serverTotalShiftCount = shiftHistoryPages?.pages[0]?.meta?.totalCount;
  // The server's totalCount includes shifts still in their grace period, but the client
  // filters those out. Subtract the filtered count to keep the displayed total accurate.
  const totalShiftCount = isDefined(serverTotalShiftCount)
    ? serverTotalShiftCount - clientFilteredShiftCount
    : undefined;

  const filtersModalState = useModalState();

  return (
    <WorkplaceModalsDataProvider
      baseNavigationPath={`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`}
    >
      <ThemeProvider theme={getTheme()}>
        <FormProvider<FiltersFormValues> {...form}>
          <PageWrapper variant="tertiary" sx={{ position: "relative" }}>
            <CollapsiblePageHeader
              scrollContainerRef={scrollRef}
              fallbackGoBackPath={SHIFT_HISTORY_FULL_PATH}
              rightAction={
                <Badge
                  count={changedFiltersCount}
                  overlap="circular"
                  color="primary"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <IconButton
                    isSelected={changedFiltersCount > 0}
                    variant="outlined"
                    aria-label={
                      changedFiltersCount > 0
                        ? `Open Filters Button (${changedFiltersCount} applied)`
                        : "Open Filters Button"
                    }
                    size="medium"
                    iconType="slider-settings"
                    onClick={() => {
                      filtersModalState.openModal();
                    }}
                  />
                </Badge>
              }
              collapsedTitle={
                <Text semibold variant="h6" align="center">
                  Shift history
                </Text>
              }
              headerSx={(theme) => ({
                background: theme.palette.shift?.history?.gradient,
              })}
              topRowSx={{ paddingX: 6 }}
            >
              <Stack spacing={1} sx={{ paddingX: 6, paddingTop: 6, paddingBottom: 7 }}>
                {isDefined(totalShiftCount) && totalShiftCount > 0 && (
                  <Text semibold variant="body1">
                    {totalShiftCount} shifts worked
                  </Text>
                )}
                <Title component="h3" variant="h3">
                  Shift history
                </Title>
              </Stack>
            </CollapsiblePageHeader>
            <PageContent
              ref={scrollRef}
              sx={{
                paddingTop: 5,
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <PullToRefresh onRefresh={refetchShifts} />
              <ShiftHistoryBannerList
                isCheckingGeofence={isCheckingGeofence}
                selectedShift={selectedShift}
                onUploadTimesheetClick={handleUploadTimesheetClick}
                onRequestShiftAdjustmentClick={handleRequestShiftAdjustment}
              />
              {isInitialLoading && (
                <ShiftHistoryLoadingPageState
                  isRedesignEnabled={isShiftHistoryPageRedesignEnabled}
                />
              )}
              {isError && <Alert severity="error">Error loading shift history.</Alert>}
              {isSuccess && shiftHistory.length > 0 && (
                <Virtuoso
                  data={shiftHistory}
                  customScrollParent={scrollRef.current ?? undefined}
                  width="100%"
                  increaseViewportBy={2000}
                  topItemCount={0}
                  overscan={5}
                  endReached={() => {
                    if (hasNextPage) {
                      void fetchNextPage();
                    }
                  }}
                  components={{
                    Header: () => {
                      if (!isShiftHistoryPageRedesignEnabled) {
                        return null;
                      }

                      return (
                        <Stack direction="row" alignItems="center" spacing={3}>
                          <Text semibold variant="h4">
                            Past shifts
                          </Text>
                          {isDefined(totalShiftCount) && totalShiftCount > 0 && (
                            <Pill label={String(totalShiftCount)} size="small" variant="filled" />
                          )}
                        </Stack>
                      );
                    },
                    Footer: () => {
                      if (isFetchingNextPage) {
                        return (
                          <Stack px={5} minHeight="76px">
                            <Stack alignItems="center" py={8}>
                              <LoadingSpinner size="medium" variant="primary" />
                              <Text
                                mt={4}
                                pb={10}
                                color={(theme: Theme) => theme.palette.text.secondary}
                              >
                                Loading more shifts...
                              </Text>
                            </Stack>
                          </Stack>
                        );
                      }

                      return <Stack pb={6} />;
                    },
                  }}
                  itemContent={(_, shift) => {
                    const workplaceId = shift.relationships?.workplace?.data?.id;
                    if (!workplaceId) {
                      return null;
                    }

                    const workplaceInfo = workplaceMap.get(workplaceId);

                    const hasPendingExtraWorkedTimeRequest =
                      shift.relationships?.extraWorkedTimeRequest?.data?.some(({ id }) => {
                        const request = extraWorkedTimeRequestMap.get(id);
                        return request?.attributes?.status === ExtraWorkedTimeRequestStatus.PENDING;
                      }) ?? false;

                    const approvedBreakTimeInMinutes =
                      shift.relationships?.extraWorkedTimeRequest?.data?.reduce(
                        (accumulator, { id }) => {
                          const requestData = extraWorkedTimeRequestMap.get(id);
                          if (!isDefined(requestData?.attributes)) {
                            return accumulator;
                          }

                          const { attributes: request } = requestData;

                          if (
                            request.type !== ExtraWorkedTimeType.BREAK ||
                            request.status !== ExtraWorkedTimeRequestStatus.APPROVED
                          ) {
                            return accumulator;
                          }

                          if (isDefined(request.end) && isDefined(request.start)) {
                            return (
                              accumulator +
                              differenceInMinutes(parseISO(request.end), parseISO(request.start))
                            );
                          }

                          return accumulator;
                        },
                        0
                      ) ?? 0;

                    const totalShiftTimeInHours =
                      (shift.attributes?.schedule?.durationWithoutBreaksInHours ?? 0) +
                      approvedBreakTimeInMinutes / 60;

                    if (isShiftHistoryPageRedesignEnabled) {
                      const relevantRequest = getRelevantExtraWorkedTimeRequest(
                        shift,
                        extraWorkedTimeRequestMap
                      );

                      return (
                        <ShiftHistoryListItem
                          isVerified={shift.attributes?.verified ?? false}
                          shiftStartDate={shift.attributes?.schedule?.startAt}
                          clockInTime={shift.attributes?.clockInOut?.start}
                          clockOutTime={shift.attributes?.clockInOut?.end}
                          workplaceName={workplaceInfo?.name}
                          workplaceTimezone={workplaceInfo?.timezone}
                          hourlyRate={shift.attributes?.pay?.total ?? 0}
                          totalShiftTimeInHours={totalShiftTimeInHours}
                          request={relevantRequest}
                          onClick={() => {
                            handleShiftClick(shift.id);
                          }}
                        />
                      );
                    }

                    const actionParams: ShiftActionParams = {
                      shiftId: shift.id,
                      workplaceId,
                      workplaceLocation: workplaceInfo?.geoLocation,
                      shiftEndAt: shift.attributes?.schedule?.endAt,
                      isVerified: shift.attributes?.verified ?? false,
                      isTimesheetUploaded: (shift.attributes?.timecard?.files?.length ?? 0) > 0,
                    };

                    return (
                      <ShiftHistoryCard
                        key={shift.id}
                        shift={shift}
                        workplaceInfo={workplaceInfo}
                        totalShiftTimeInHours={totalShiftTimeInHours}
                        isMissedPunchRequestEnabled={
                          shift.attributes?.isMissedPunchRequestEnabled ?? false
                        }
                        hasPendingExtraWorkedTimeRequest={hasPendingExtraWorkedTimeRequest}
                        isTimesheetUploaded={actionParams.isTimesheetUploaded}
                        shouldDisableButton={isCheckingGeofence}
                        isRequestingShiftAdjustment={
                          selectedShift?.shiftId === shift.id && isCheckingGeofence
                        }
                        onUploadTimesheetClick={() => {
                          handleUploadTimesheetClick(actionParams);
                        }}
                        onChatWithSupportClick={() => {
                          handleChatWithSupportClick(actionParams);
                        }}
                        onRequestShiftAdjustmentClick={() => {
                          setSelectedShift(actionParams);
                          void handleRequestShiftAdjustment(actionParams);
                        }}
                      />
                    );
                  }}
                />
              )}
              {isSuccess && shiftHistory.length === 0 && (
                <ShiftHistoryEmptyState filtersAreApplied={changedFiltersCount > 0} />
              )}
            </PageContent>
            {isShiftHistoryPageRedesignEnabled && !shouldOnboardToStripe && (
              <FloatingNavigationButton
                replace
                label="View earnings"
                href={EARNINGS_FULL_PATH}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_VIEW_EARNINGS_TAPPED);
                }}
              />
            )}
            <ShiftHistoryFiltersBottomSheet
              modalState={filtersModalState}
              changedFiltersCount={changedFiltersCount}
              resetFilters={resetFilters}
              cancelFilters={cancelFilters}
              onSubmit={submitFilters}
            />
            <UploadTimesheetBottomSheet
              showDescriptionText
              modalState={uploadTimesheetModalState}
              shiftId={selectedShift?.shiftId}
              isTimesheetUploaded={selectedShift?.isTimesheetUploaded ?? false}
              onUploadStarted={() => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_STARTED, {
                  shiftId: selectedShift?.shiftId,
                  workerId: worker.userId,
                });
              }}
              onSuccess={async ({ uploadDurationInMs, updateTimecardDurationInMs }) => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_UPLOADED, {
                  shiftId: selectedShift?.shiftId,
                  workerId: worker.userId,
                  isReupload: selectedShift?.isTimesheetUploaded ?? false,
                  uploadDurationInMs,
                  updateTimecardDurationInMs,
                });
                setSelectedShift(undefined);
                await refetchShifts();
              }}
              onError={(error) => {
                logEvent(APP_V2_USER_EVENTS.SHIFT_HISTORY_UPLOAD_TIMESHEET_UPLOAD_ERROR, {
                  shiftId: selectedShift?.shiftId,
                  workerId: worker.userId,
                  error: error?.message ?? "Unknown error",
                });
                setSelectedShift(undefined);
              }}
            />
            {isDefined(selectedShift?.shiftId) && (
              <ChatWithSupportBottomSheet
                modalState={chatWithSupportModalState}
                shiftId={selectedShift.shiftId}
              />
            )}
            <ReturnForSignatureBottomSheet
              modalState={returnForSignatureModalState}
              shiftId={selectedShift?.shiftId ?? ""}
              workerId={worker.userId}
              isCheckingLocation={false}
              isShiftVerified={selectedShift?.isVerified ?? false}
              onRecheckLocation={handleRecheckLocation}
              onCancel={() => {
                returnForSignatureModalState.closeModal();
                setSelectedShift(undefined);
              }}
              onConfirm={handleConfirmWithoutSignature}
            />
          </PageWrapper>
        </FormProvider>
      </ThemeProvider>
    </WorkplaceModalsDataProvider>
  );
}
/* eslint-enable max-lines */
